import {httpSubmitSurvey} from '../services/submitSurvey.http.ts'
import {useEffect} from 'react'
import {errorHandler} from '@utilities/helpers.ts'
import {useTranslation} from 'react-i18next'
import {useChatbotStore} from '../store/chatbotStore.ts'
import {useAuthStore} from '@features/user/store/store.ts'
import {ChatbotStep} from '../types.ts'

const valueClassMap: Record<number, string> = {
    1: 'veryPoor',
    2: 'poor',
    3: 'average',
    4: 'good',
    5: 'excellent'
}

interface SurveySubmitProps {
    steps?: Record<
        string,
        {
            value?: string
            id?: string
        }
    >
}

/**
 * Component that handles survey submission after chatbot completion.
 * Extracts survey data from chatbot steps and submits to backend.
 */
export const SurveySubmit = ({steps}: SurveySubmitProps) => {
    const {t} = useTranslation()
    const accessToken = useAuthStore(store => store.accessToken)
    const sessionId = useChatbotStore(store => store.sessionId)
    const chatbotRef = useChatbotStore(store => store.chatbotRef)

    // Extract chatbot steps from internal state (more reliable than store history)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chatbotSteps = (chatbotRef?.current as any)?.state?.renderedSteps

    // Extract survey data with fallback to props
    const surveyRating =
        chatbotSteps?.find((step: ChatbotStep) => step.id === 's_options_rating')?.value ||
        steps?.s_options_rating?.value

    const surveyNotes =
        chatbotSteps?.find((step: ChatbotStep) => step.id === 's_user_comment')?.value ||
        steps?.s_user_comment?.value ||
        'No comment'

    // Build flow string from chatbot steps (excluding first step)
    const flow =
        chatbotSteps && Object.values(chatbotSteps).length > 0
            ? Object.values(chatbotSteps)
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((step: any) => step?.id)
                  .filter((id: string | undefined): id is string => Boolean(id?.trim()))
                  .slice(1)
                  .join(', ')
            : 'survey_flow'

    useEffect(() => {
        onSendSurveyResults()
    }, [])

    const onSendSurveyResults = async (): Promise<void> => {
        if (!accessToken || !surveyRating || !sessionId) {
            console.error('Survey submission failed: Missing required data', {
                hasAccessToken: !!accessToken,
                hasSurveyRating: !!surveyRating,
                hasSessionId: !!sessionId
            })
            return
        }

        try {
            await httpSubmitSurvey({
                flow,
                notes: surveyNotes,
                rating: parseInt(surveyRating),
                thread_id: sessionId
            })
        } catch (error) {
            errorHandler(error)
        }
    }

    const getRatingText = (rating: string): string => {
        const numericRating = parseInt(rating, 10)
        return valueClassMap[numericRating] || ''
    }

    return (
        <div>
            <p>{t('chatbot:survey_submit:survey_submit_recap')}</p>
            {surveyRating && (
                <p>
                    {t('chatbot:survey_submit:survey_submit_rating')} {getRatingText(surveyRating)}
                </p>
            )}
            <p>
                {t('chatbot:survey_submit:survey_submit_comment')} {surveyNotes}
            </p>
        </div>
    )
}
